import { supabase } from '../lib/supabase';

/**
 * 이미지 파일을 Supabase Storage에 업로드하고 public URL을 반환
 * @param file - 업로드할 이미지 파일
 * @param folder - 저장할 폴더 (선택사항, 기본값: 'courses')
 * @returns 업로드된 이미지의 public URL
 */
export async function uploadImageToStorage(
  file: File,
  folder: string = 'courses'
): Promise<string> {
  try {
    // 파일 확장자 추출
    const fileExt = file.name.split('.').pop();
    // 고유한 파일명 생성 (타임스탬프 + 랜덤 문자열)
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
    const filePath = `${folder}/${fileName}`;

    // Supabase Storage에 업로드
    const { error } = await supabase.storage
      .from('program-images')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) {
      console.error('Image upload error:', error);
      throw new Error(`이미지 업로드 실패: ${error.message}`);
    }

    // Public URL 생성
    const { data: { publicUrl } } = supabase.storage
      .from('program-images')
      .getPublicUrl(filePath);

    return publicUrl;
  } catch (error) {
    console.error('Error in uploadImageToStorage:', error);
    throw error;
  }
}

/**
 * base64 문자열을 File 객체로 변환
 * @param base64String - base64 이미지 문자열 (data:image/jpeg;base64,... 형식)
 * @param fileName - 파일명
 * @returns File 객체
 */
export function base64ToFile(base64String: string, fileName: string = 'image.jpg'): File {
  // data:image/jpeg;base64, 부분 제거
  const base64Data = base64String.split(',')[1] || base64String;

  // base64를 binary로 변환
  const binaryString = atob(base64Data);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  // MIME 타입 추출 (없으면 기본값 사용)
  const mimeMatch = base64String.match(/data:([^;]+);/);
  const mimeType = mimeMatch ? mimeMatch[1] : 'image/jpeg';

  // Blob 생성 후 File로 변환
  const blob = new Blob([bytes], { type: mimeType });
  return new File([blob], fileName, { type: mimeType });
}

/**
 * Storage의 이미지 파일 삭제
 * @param imageUrl - 삭제할 이미지의 URL
 */
export async function deleteImageFromStorage(imageUrl: string): Promise<void> {
  try {
    // URL에서 파일 경로 추출
    // 예: https://xxx.supabase.co/storage/v1/object/public/program-images/courses/123.jpg
    // -> courses/123.jpg
    const urlParts = imageUrl.split('/program-images/');
    if (urlParts.length < 2) {
      console.warn('Invalid image URL format:', imageUrl);
      return;
    }

    const filePath = urlParts[1];

    const { error } = await supabase.storage
      .from('program-images')
      .remove([filePath]);

    if (error) {
      console.error('Image delete error:', error);
      throw new Error(`이미지 삭제 실패: ${error.message}`);
    }
  } catch (error) {
    console.error('Error in deleteImageFromStorage:', error);
    // 삭제 실패는 치명적이지 않으므로 에러를 throw하지 않음
  }
}
